import React, { useEffect, useState } from "react";
import { Mail, Phone, MapPin } from "lucide-react";
import ContactForm from "../components/ContactForm";
import SchemaMarkup, { buildBreadcrumb } from "../components/SchemaMarkup";
import useDocumentMeta from "../hooks/useDocumentMeta";
import { api } from "../lib/api";
import { useI18n } from "../i18n/I18nProvider";

const FALLBACK = {
  title: "Contact",
  eyebrow: "Speak With Us",
  heading: "A senior advisor will <em>respond personally</em> within 24 hours.",
  intro: "<p>Whether you would like the updated price list, a brochure for a single development or a private tour of the resort — start the conversation here.</p>",
  email: "advisors@cortesin-developments.com",
  phone: "+34 952 000 000",
  address: "Finca Cortesín Resort\n29690 Casares, Málaga, Spain",
  seo: { title: "Contact | Finca Cortesin New Developments Advisor", description: "Speak with a local property advisor about new developments in Finca Cortesin, Casares." },
};

export default function Contact() {
  const [page, setPage] = useState(FALLBACK);
  const { t } = useI18n();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/pages/contact");
        setPage({ ...FALLBACK, ...data });
      } catch (_err) { /* ignore */ }
    })();
  }, []);

  useDocumentMeta({ title: page.seo?.title, description: page.seo?.description });

  return (
    <div className="bg-[#F9F6F0] text-[#1A1A1A] min-h-screen">
      <SchemaMarkup schema={buildBreadcrumb([{ name: t("home"), url: "/" }, { name: t("contact_breadcrumb"), url: "/contact" }])} />

      <section className="pt-40 pb-16 lg:pb-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#73706B] mb-5">— {page.eyebrow}</p>
            <h1 data-testid="contact-title" className="text-5xl sm:text-6xl lg:text-7xl font-light leading-[0.95]" style={{ fontFamily: "'Cormorant Garamond', serif" }} dangerouslySetInnerHTML={{ __html: page.heading }} />
            <div className="prose prose-stone max-w-none text-base text-[#3D3B38] mt-8 leading-relaxed" dangerouslySetInnerHTML={{ __html: page.intro || "" }} />

            <div className="mt-12 space-y-6">
              <div className="flex items-start gap-4 border-b border-[#E5DFD5] pb-4">
                <Mail className="w-4 h-4 mt-1 text-[#C5A880]" strokeWidth={1.5} />
                <div>
                  <p className="text-[10px] tracking-[0.25em] uppercase text-[#73706B] mb-1">{t("contact_label_email")}</p>
                  <p className="text-base">{page.email}</p>
                </div>
              </div>
              <div className="flex items-start gap-4 border-b border-[#E5DFD5] pb-4">
                <Phone className="w-4 h-4 mt-1 text-[#C5A880]" strokeWidth={1.5} />
                <div>
                  <p className="text-[10px] tracking-[0.25em] uppercase text-[#73706B] mb-1">{t("contact_label_phone")}</p>
                  <p className="text-base">{page.phone}</p>
                </div>
              </div>
              <div className="flex items-start gap-4 border-b border-[#E5DFD5] pb-4">
                <MapPin className="w-4 h-4 mt-1 text-[#C5A880]" strokeWidth={1.5} />
                <div>
                  <p className="text-[10px] tracking-[0.25em] uppercase text-[#73706B] mb-1">{t("contact_label_office")}</p>
                  <p className="text-base whitespace-pre-line">{page.address}</p>
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="border border-[#E5DFD5] bg-[#FFFFFF] p-8 lg:p-12">
              <ContactForm testIdPrefix="contact-page" sourcePage="Contact page" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
